import resizeHandlePositions from '../../../common/constants/resize-handle-positions';
import { alignmentTypes, alignmentPoints } from '../../../common/constants/alignment-types';
import calculateResizedBounds from './calculate-resized-bounds';
import axis from '../constants/axis';

// Helper functions to check positioning area bounds
const isWithinHorizontalBounds = (bounds, areaBounds, direction) =>
  (bounds.right <= areaBounds.right && direction.endsWith(alignmentPoints.RIGHT)) ||
  (bounds.left >= areaBounds.left && direction.endsWith(alignmentPoints.LEFT));

const isWithinVerticalBounds = (bounds, areaBounds, direction) =>
  (bounds.bottom <= areaBounds.bottom && direction.startsWith(alignmentPoints.BOTTOM)) ||
  (bounds.top >= areaBounds.top && direction.startsWith(alignmentPoints.TOP));

const isPureHorizontalResize = (direction) =>
  !direction.startsWith(alignmentPoints.BOTTOM) && !direction.startsWith(alignmentPoints.TOP);

const isPureVerticalResize = (direction) =>
  !direction.endsWith(alignmentPoints.RIGHT) && !direction.endsWith(alignmentPoints.LEFT);

function createLeftOffsets({
  targetLeft,
  targetRight,
  targetCenterX,
  resizingLeft,
  alignmentTypesConst,
}) {
  return [
    { value: targetLeft - resizingLeft, type: alignmentTypesConst.LEFT_LEFT },
    { value: targetRight - resizingLeft, type: alignmentTypesConst.LEFT_RIGHT },
    { value: targetCenterX - resizingLeft, type: alignmentTypesConst.LEFT_CENTER },
  ];
}

function createRightOffsets({
  targetLeft,
  targetRight,
  targetCenterX,
  resizingRight,
  alignmentTypesConst,
}) {
  return [
    { value: targetCenterX - resizingRight, type: alignmentTypesConst.RIGHT_CENTER },
    { value: targetLeft - resizingRight, type: alignmentTypesConst.RIGHT_LEFT },
    { value: targetRight - resizingRight, type: alignmentTypesConst.RIGHT_RIGHT },
  ];
}

function createTopOffsets({
  targetTop,
  targetBottom,
  targetCenterY,
  resizingTop,
  alignmentTypesConst,
}) {
  return [
    { value: targetTop - resizingTop, type: alignmentTypesConst.TOP_TOP },
    { value: targetBottom - resizingTop, type: alignmentTypesConst.TOP_BOTTOM },
    { value: targetCenterY - resizingTop, type: alignmentTypesConst.TOP_CENTER },
  ];
}

function createBottomOffsets({
  targetTop,
  targetBottom,
  targetCenterY,
  resizingBottom,
  alignmentTypesConst,
}) {
  return [
    { value: targetTop - resizingBottom, type: alignmentTypesConst.BOTTOM_TOP },
    { value: targetBottom - resizingBottom, type: alignmentTypesConst.BOTTOM_BOTTOM },
    { value: targetCenterY - resizingBottom, type: alignmentTypesConst.BOTTOM_CENTER },
  ];
}

function getOffsetsForDirection({
  resizeDirection,
  targetLeft,
  targetRight,
  targetTop,
  targetBottom,
  targetCenterX,
  targetCenterY,
  resizingLeft,
  resizingRight,
  resizingTop,
  resizingBottom,
  alignmentTypesConst,
}) {
  let xOffsets = [];
  let yOffsets = [];

  switch (resizeDirection) {
    case resizeHandlePositions.TOP_LEFT:
      xOffsets = createLeftOffsets({
        targetLeft,
        targetRight,
        targetCenterX,
        resizingLeft,
        alignmentTypesConst,
      });
      yOffsets = createTopOffsets({
        targetTop,
        targetBottom,
        targetCenterY,
        resizingTop,
        alignmentTypesConst,
      });
      break;

    case resizeHandlePositions.TOP_CENTER:
      yOffsets = createTopOffsets({
        targetTop,
        targetBottom,
        targetCenterY,
        resizingTop,
        alignmentTypesConst,
      });
      break;

    case resizeHandlePositions.TOP_RIGHT:
      xOffsets = createRightOffsets({
        targetLeft,
        targetRight,
        targetCenterX,
        resizingRight,
        alignmentTypesConst,
      });
      yOffsets = createTopOffsets({
        targetTop,
        targetBottom,
        targetCenterY,
        resizingTop,
        alignmentTypesConst,
      });
      break;

    case resizeHandlePositions.CENTER_RIGHT:
      xOffsets = createRightOffsets({
        targetLeft,
        targetRight,
        targetCenterX,
        resizingRight,
        alignmentTypesConst,
      });
      break;

    case resizeHandlePositions.CENTER_LEFT:
      xOffsets = createLeftOffsets({
        targetLeft,
        targetRight,
        targetCenterX,
        resizingLeft,
        alignmentTypesConst,
      });
      break;

    case resizeHandlePositions.BOTTOM_LEFT:
      xOffsets = createLeftOffsets({
        targetLeft,
        targetRight,
        targetCenterX,
        resizingLeft,
        alignmentTypesConst,
      });
      yOffsets = createBottomOffsets({
        targetTop,
        targetBottom,
        targetCenterY,
        resizingBottom,
        alignmentTypesConst,
      });
      break;

    case resizeHandlePositions.BOTTOM_CENTER:
      yOffsets = createBottomOffsets({
        targetTop,
        targetBottom,
        targetCenterY,
        resizingBottom,
        alignmentTypesConst,
      });
      break;

    case resizeHandlePositions.BOTTOM_RIGHT:
      xOffsets = createRightOffsets({
        targetLeft,
        targetRight,
        targetCenterX,
        resizingRight,
        alignmentTypesConst,
      });
      yOffsets = createBottomOffsets({
        targetTop,
        targetBottom,
        targetCenterY,
        resizingBottom,
        alignmentTypesConst,
      });
      break;

    default:
      break;
  }

  return { xOffsets, yOffsets };
}

function calculateResizeCorrection({
  elementBounds,
  positioningAreaBounds,
  targetElementBounds,
  element,
  correctionDistance = 5,
  resizeDirection,
}) {
  // Helper function to calculate corrections for an axis (optimized)
  const calculateAxisCorrections = (offsets, threshold, axisName, targetBounds) => {
    const result = offsets.reduce(
      (acc, offset) => {
        const { value, type: alignmentType } = offset;

        // Skip zero values early
        if (value === 0) {
          return acc;
        }

        const abs = Math.abs(value);

        // Only process if it's within threshold and smaller than current smallest
        if (abs < threshold && abs < acc.smallestAbs) {
          return {
            smallestCorrection:
              (axisName === axis.X && alignmentType.startsWith(`${alignmentPoints.LEFT}-`)) ||
              (axisName === axis.Y && alignmentType.startsWith(`${alignmentPoints.TOP}-`))
                ? -1 * value
                : value,
            smallestAbs: abs,
            alignmentType,
          };
        }

        return acc;
      },
      {
        smallestCorrection: null,
        smallestAbs: threshold,
        alignmentType: null,
      }
    );

    return {
      correction: result.smallestCorrection || 0,
      meta:
        result.smallestCorrection !== null
          ? {
              axis: axisName,
              alignmentType: result.alignmentType,
              elementBounds: targetBounds,
            }
          : null,
    };
  };

  // Pre-calculate bounds values to avoid repeated property access
  const {
    left: targetLeft,
    right: targetRight,
    top: targetTop,
    bottom: targetBottom,
  } = targetElementBounds;
  const {
    left: resizingLeft,
    right: resizingRight,
    top: resizingTop,
    bottom: resizingBottom,
  } = elementBounds;

  // Calculate center points
  const targetCenterX = targetLeft + (targetRight - targetLeft) / 2;
  const targetCenterY = targetTop + (targetBottom - targetTop) / 2;

  const { xOffsets, yOffsets } = getOffsetsForDirection({
    resizeDirection,
    targetLeft,
    targetRight,
    targetTop,
    targetBottom,
    targetCenterX,
    targetCenterY,
    resizingLeft,
    resizingRight,
    resizingTop,
    resizingBottom,
    alignmentTypesConst: alignmentTypes,
  });

  let xResult = null;
  let yResult = null;

  // We need this check to not calculate corrections if the element is going to be outside
  // the positioning area
  if (isWithinHorizontalBounds(elementBounds, positioningAreaBounds, resizeDirection)) {
    if (
      isPureHorizontalResize(resizeDirection) ||
      isWithinVerticalBounds(elementBounds, positioningAreaBounds, resizeDirection)
    ) {
      // If the resize direction is horizontal, we can skip the Y axis correction
      xResult = calculateAxisCorrections(xOffsets, correctionDistance, axis.X, targetElementBounds);
    }
  }

  if (isWithinVerticalBounds(elementBounds, positioningAreaBounds, resizeDirection)) {
    if (
      isPureVerticalResize(resizeDirection) ||
      isWithinHorizontalBounds(elementBounds, positioningAreaBounds, resizeDirection)
    ) {
      yResult = calculateAxisCorrections(yOffsets, correctionDistance, axis.Y, targetElementBounds);
    }
  }

  // Collect meta information about applied corrections
  const appliedCorrections = [];

  if (xResult?.meta) {
    appliedCorrections.push(xResult.meta);
  }

  if (yResult?.meta) {
    appliedCorrections.push(yResult.meta);
  }

  return {
    x: xResult?.correction || 0,
    y: yResult?.correction || 0,
    targetElement: element,
    meta: {
      appliedCorrections,
      hasCorrection: appliedCorrections.length > 0,
      correctionDistance,
    },
  };
}

function computeResizeCorrection({
  initialElementBounds,
  initialAspectRatio,
  elementOffsetRatio,
  positioningAreaBounds,
  distance,
  correctionDistance = 5,
  resizeDirection,
  elements = [],
}) {
  const elementBounds = calculateResizedBounds({
    initialBounds: initialElementBounds,
    resizeDirection,
    elementOffsetRatio,
    distance,
    initialAspectRatio,
  });

  const allCorrections = elements.map(({ elementBounds: targetElementBounds, element }) =>
    calculateResizeCorrection({
      initialElementBounds,
      elementBounds,
      positioningAreaBounds,
      targetElementBounds,
      distance,
      element,
      correctionDistance,
      resizeDirection,
    })
  );

  // Helper function to process corrections for a specific axis
  const processCorrectionAxis = (correction, targetAxis, currentBest) => {
    const value = targetAxis === axis.X ? correction.x : correction.y;

    if (value !== 0) {
      const absValue = Math.abs(value);
      const bestAbs = targetAxis === axis.X ? currentBest.bestAbsX : currentBest.bestAbsY;
      const currentBestValue = targetAxis === axis.X ? currentBest.bestX : currentBest.bestY;

      if (currentBestValue === 0 || absValue < bestAbs) {
        const axisCorrection = correction.meta?.appliedCorrections?.find(
          (c) => c.axis === targetAxis
        );

        return {
          bestValue: value,
          bestAbs: absValue,
          correctionMeta: axisCorrection ? { ...axisCorrection, value } : null,
        };
      }
    }

    return null;
  };

  // Find the best corrections (smallest absolute values)
  const result = allCorrections.reduce(
    (acc, correction) => {
      const xResult = processCorrectionAxis(correction, axis.X, acc);

      if (xResult) {
        acc.bestX = xResult.bestValue;
        acc.bestAbsX = xResult.bestAbs;
        acc.xCorrectionMeta = xResult.correctionMeta;
      }

      const yResult = processCorrectionAxis(correction, axis.Y, acc);

      if (yResult) {
        acc.bestY = yResult.bestValue;
        acc.bestAbsY = yResult.bestAbs;
        acc.yCorrectionMeta = yResult.correctionMeta;
      }

      return acc;
    },
    {
      bestX: 0,
      bestY: 0,
      bestAbsX: Infinity,
      bestAbsY: Infinity,
      xCorrectionMeta: null,
      yCorrectionMeta: null,
    }
  );

  // Choose only the smaller correction by absolute value to avoid conflicting corrections
  const appliedCorrections = [];
  let finalX = 0;
  let finalY = 0;

  if (result.bestX !== 0 && result.bestY !== 0) {
    if (result.bestAbsX <= result.bestAbsY) {
      finalX = result.bestX;

      if (result.xCorrectionMeta) {
        appliedCorrections.push(result.xCorrectionMeta);
      }
    } else {
      finalY = result.bestY;

      if (result.yCorrectionMeta) {
        appliedCorrections.push(result.yCorrectionMeta);
      }
    }
  } else {
    if (result.bestX !== 0) {
      finalX = result.bestX;

      if (result.xCorrectionMeta) {
        appliedCorrections.push(result.xCorrectionMeta);
      }
    }

    if (result.bestY !== 0) {
      finalY = result.bestY;

      if (result.yCorrectionMeta) {
        appliedCorrections.push(result.yCorrectionMeta);
      }
    }
  }

  return {
    x: finalX,
    y: finalY,
    meta: {
      appliedCorrections,
      hasCorrection: appliedCorrections.length > 0,
      totalCorrections: allCorrections.length,
    },
  };
}

export default computeResizeCorrection;
